/**
 * Module-level registry for the per-org "tasks changed" broadcast poke.
 *
 * WHY a content-free broadcast and not postgres_changes: a user who LOSES
 * access to a task (a public task goes private, or they are unassigned) can
 * NEVER receive a `postgres_changes` event for it — Supabase realtime will not
 * deliver a row the recipient can no longer SELECT under RLS. So a visibility
 * change is announced as a payload-less poke on a per-org channel; every
 * subscribed board does a debounced refetch and RLS re-gates what survives.
 *
 * SECURITY INVARIANT (government constituent data): the broadcast payload MUST
 * carry NO task data — no id, title, is_private, assignee, nothing. It is a
 * pure "something changed, refetch" signal. Correctness depends on the payload
 * being content-free + RLS on the refetch query, NOT on the channel being
 * private (the private channel is defense-in-depth only). See
 * docs/plans/pub-893-task-sharing-redo.md.
 *
 * The receive side (`useTasksSyncRealtime`) registers its channel here via
 * `registerTasksSyncChannel`; the mutating client calls
 * `broadcastTasksChanged(orgId)` after a visibility-relevant mutation. The poke
 * reaches OTHER members' boards only: Supabase `broadcast.self` defaults to
 * `false`, so the sending client receives no echo of its own poke. The actor's
 * own board updates via its mutation's cache invalidation instead.
 *
 * `useTasksSyncRealtime` is mounted at APP ROOT (TasksSyncRealtimeMount in
 * App.tsx), not just on the Tasks board, so the actor always has a JOINED
 * sender channel for the current org - a visibility mutation made while OFF the
 * board (detail panel, correspondence view, quick action) still pokes other
 * members' boards (RC-3). Without the app-root mount the send below no-ops
 * whenever the actor is not on a board and the poke is silently dropped.
 */
import { REALTIME_CHANNEL_STATES } from '@supabase/supabase-js';

import type { RealtimeChannel } from '@supabase/supabase-js';

/** Broadcast event name for a content-free "tasks changed" poke. */
export const TASKS_CHANGED_EVENT = 'tasks.changed';

/** Builds the per-org tasks-sync channel topic. */
export function buildTasksSyncTopic(orgId: string): string {
  return `tasks-sync:${orgId}`;
}

// orgId -> the set of live channels subscribed for that org. A Set (not a
// single channel) tolerates the StrictMode mount→cleanup→mount window where two
// channels for the same org briefly coexist.
let channelsByOrg = new Map<string, Set<RealtimeChannel>>();

/** Register a subscribed tasks-sync channel so the org can be broadcast to. */
export function registerTasksSyncChannel(orgId: string, channel: RealtimeChannel): void {
  let channels = channelsByOrg.get(orgId);
  if (!channels) {
    channels = new Set<RealtimeChannel>();
    channelsByOrg.set(orgId, channels);
  }
  channels.add(channel);
}

/** Drop a torn-down channel from the registry. */
export function unregisterTasksSyncChannel(orgId: string, channel: RealtimeChannel | null): void {
  if (!channel) {
    return;
  }
  const channels = channelsByOrg.get(orgId);
  if (!channels) {
    return;
  }
  channels.delete(channel);
  if (channels.size === 0) {
    channelsByOrg.delete(orgId);
  }
}

/**
 * Broadcast a content-free "tasks changed" poke to the org channel.
 *
 * Payload is always `{}` — the security invariant. No-ops when no channel is
 * registered for the org (the actor's own board still updates via its mutation
 * invalidation; the poke only serves OTHER members).
 *
 * Only sends to channels in the JOINED state. A channel registers at
 * channel-create time, before the websocket has finished joining; sending into
 * that window is silently dropped by the SDK. A not-yet-joined board refetches
 * on first load anyway, so skipping it is correct. Mirrors the comment-room
 * registry's joined-state filter (`getPreferredActiveChannel`).
 */
export function broadcastTasksChanged(orgId: string | null | undefined): void {
  sendOnJoinedChannels(orgId, TASKS_CHANGED_EVENT);
}

/**
 * Content-free pokes for the agentic home, on the same office room. Each names which of the
 * home's reads to refresh and carries nothing else (payload `{}`, the invariant above). Sent only
 * in an office whose agentic home is on (`enableOfficeSync`), and received only by the
 * app-root mount there (`useTasksSyncRealtime` with `officeSync`), so other offices see no new
 * traffic. See docs/plans/2026-10-17-fix-agentic-home-instant-panels-plan.md, decision 5.
 */
export const OFFICE_CHANGED_EVENTS = {
  /** A draft's approval moved: requested, approved, sent back or recalled. */
  approvals: 'approvals.changed',
  /** An office agent was saved or archived. */
  agents: 'office-agents.changed',
  /** A document joined or left the Knowledge Library. */
  knowledge: 'knowledge.changed',
  /**
   * Who a task is due to changed in a way its activity cannot tell the person who lost it: a bulk
   * assignment, or a representation's owners moving with its status.
   */
  tasksDue: 'tasks.due-changed',
} as const;

export type OfficeChangedEvent = (typeof OFFICE_CHANGED_EVENTS)[keyof typeof OFFICE_CHANGED_EVENTS];

let officeSyncOrgs = new Set<string>();

/**
 * Records that an office's agentic home is on, which is when its office pokes are sent. Returns
 * what records it off again.
 */
export function enableOfficeSync(orgId: string): () => void {
  officeSyncOrgs.add(orgId);
  return () => {
    officeSyncOrgs.delete(orgId);
  };
}

/**
 * The office whose pokes this tab sends now (the current one, where its agentic home is on), for
 * a write that does not name its office: captured when the write starts, so a change of office
 * while it is out pokes the office it was made in.
 */
export function currentOfficeSyncOrg(): string | null {
  const [orgId] = officeSyncOrgs;
  return orgId ?? null;
}

/**
 * Pokes the office's other members, and the sender's own other tabs, about one of the home's
 * reads. Nothing where the office's agentic home is off.
 */
export function broadcastOfficeChanged(
  orgId: string | null | undefined,
  event: OfficeChangedEvent,
): void {
  if (orgId && officeSyncOrgs.has(orgId)) {
    sendOnJoinedChannels(orgId, event);
  }
}

/**
 * Someone may have been taken off a task, which its activity cannot tell them: the office's boards
 * read their tasks again, as for a visibility change, and its agentic homes (where on) their due
 * tasks.
 */
export function broadcastTaskAccessChanged(orgId: string | null | undefined): void {
  broadcastTasksChanged(orgId);
  broadcastOfficeChanged(orgId, OFFICE_CHANGED_EVENTS.tasksDue);
}

function sendOnJoinedChannels(orgId: string | null | undefined, event: string): void {
  if (!orgId) {
    return;
  }
  const channels = channelsByOrg.get(orgId);
  if (!channels || channels.size === 0) {
    return;
  }
  // send() is async, so it cannot mutate `channels` mid-iteration; snapshot first if that ever changes.
  for (const channel of channels) {
    if (channel.state !== REALTIME_CHANNEL_STATES.joined) {
      continue;
    }
    channel.send({ type: 'broadcast', event, payload: {} }).catch(() => null);
  }
}

export function __resetTasksSyncChannelsForTests(): void {
  channelsByOrg = new Map<string, Set<RealtimeChannel>>();
  officeSyncOrgs = new Set<string>();
}
